import type { ComponentProps, ReactNode } from "react";

import { useState } from "react";

import {
  DeprecatedTabs,
  DeprecatedTabsContent,
  DeprecatedTabsList,
  DeprecatedTabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

export interface PDFSidebarTab<TTabKey extends string = string> {
  key: TTabKey;
  icon: ReactNode;
  title: string;
  content: ReactNode;
  triggerProps?: Partial<ComponentProps<typeof DeprecatedTabsTrigger>>;
}

interface PDFSidebarProps<TTabKey extends string> {
  tabs: PDFSidebarTab<TTabKey>[];
  selectedTab: TTabKey | null;
  onTabChange: (tab: TTabKey | null) => void;
}

const collapsedTabsValue = "";

export const PDFSidebar = <TTabKey extends string>({
  tabs,
  selectedTab,
  onTabChange,
}: PDFSidebarProps<TTabKey>) => {
  return (
    <DeprecatedTabs
      orientation="vertical"
      className="af-flex"
      // collapsed tabs are represented by an empty string `""`
      value={selectedTab ?? collapsedTabsValue}
      onValueChange={() => {
        // Ignore onValueChange to support collapsable tabs (via onClick handler)
      }}
    >
      <DeprecatedTabsList className="af-rounded-none af-border-r af-border-border af-bg-transparent af-py-0 af-pl-0">
        {tabs.map((tab) => (
          <Tooltip key={tab.key}>
            <DeprecatedTabsTrigger
              value={tab.key}
              className={cn("!af-p-3", tab.triggerProps?.className)}
              asChild
              onClick={() => {
                const newSelectedTab = tab.key === selectedTab ? null : tab.key;

                onTabChange(newSelectedTab);
              }}
              {...tab.triggerProps}
            >
              <TooltipTrigger>{tab.icon}</TooltipTrigger>
            </DeprecatedTabsTrigger>
            <TooltipContent>{tab.title}</TooltipContent>
          </Tooltip>
        ))}
      </DeprecatedTabsList>

      {tabs.map((tab) => (
        <DeprecatedTabsContent
          key={tab.key}
          className="af-w-[200px] af-border-r af-border-border af-bg-interface-background"
          value={tab.key}
          hidden={tab.key !== selectedTab}
          forceMount
        >
          {tab.content}
        </DeprecatedTabsContent>
      ))}
    </DeprecatedTabs>
  );
};

interface UsePDFSidebarTabsOptions<TTabKey extends string> {
  defaultSelectedTabKey?: TTabKey | null;
}

export const usePDFSidebarTabs = <TTabKey extends string>(
  _tabs: PDFSidebarTab<TTabKey>[],
  { defaultSelectedTabKey = null }: UsePDFSidebarTabsOptions<TTabKey> = {},
) => {
  return useState<TTabKey | null>(defaultSelectedTabKey);
};
